import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useProfile } from '../../contexts/ProfileContext';
import { StartupProfile as StartupProfileType } from '../../types/firestore';
import { useTracking } from '../../utils/useTracking';
import { useFavorites } from '../../utils/useFavorites';
import { useAuth } from '../../contexts/AuthContext';
import DealTracker from '../DealTracker';
import ContactStartupModal from './ContactStartupModal';
import ContactStartupMessagePopup from './ContactStartupMessagePopup';
import FavoriteConsentPopup from './FavoriteConsentPopup';
import RemoveFavoritePopup, { type RemoveFavoriteFeedbackPayload } from './RemoveFavoritePopup';
import DismissFromFeedPopup from './DismissFromFeedPopup';
import ShareStartupPopup from './ShareStartupPopup';
import ProfileCategoryBadge from './ProfileCategoryBadge';
import {
  CATALOG_CATEGORY_LABELS,
  type CatalogCategory,
  normalizeSinceYouFollowLabel,
} from '../../constants/catalog-categories';
import { ACTION_LABELS, getNextInvestorDealStatus, USER_TYPES } from '../../constants/dealTracker';
import { PRAIRI_SURFACES } from '../../constants/prairiSurfaces';
import { useDealTracking } from '../../utils/useDealTracking';
import { logAnalyticsStartupEvent } from '../../utils/analytics';
import {
  BUSINESS_MODEL_DISPLAY_MAP,
  PRODUCT_FOCUS_DISPLAY_MAP,
  STAGE_DISPLAY_MAP,
  CURRENCY_DISPLAY_MAP,
  TRACTION_DISPLAY_MAP,
  getDisplayName,
} from '../../constants/display-mappings';
import { formatSeekingInvestment } from '../../utils/formatCompactAmount';
import { formatFounderLine } from '../../utils/formatFounderLine';
import { formatInvestorExpertise } from '../../utils/formatInvestorExpertise';
import { startupIndustryLabels } from '../../utils/startupIndustryLabels';
import { ownershipSelectOptions } from '../../constants/ownership';
import { resolveStartupId } from '../../utils/startupId';
import { markSentFounderMessage } from '../../utils/startupMessageCallout';
import { normalizeExternalUrl } from '../../utils/normalizeExternalUrl';
import { formatLinksAndMedia } from '../../utils/formatLinkDisplay';
import { isPrairiTeamName } from '../../constants/prairiTeamWelcomeMessage';
import prairiLogo from '../../assets/prairiwhite.png';
import productIcon from '../../assets/icons/product.png';
import companyIcon from '../../assets/icons/company2.png';
import { VideoPitchTile } from './VideoPitchMedia';
import { PitchDeckScrollViewer, prefetchPitchDeck } from './PitchDeckScrollViewer';
import { PrairiScrollbar, PROFILE_PITCH_THUMB_TRACK_FRACTION } from './PrairiScrollbar';
import SdgGoalImage from './SdgGoalImage';
import type {
  MessageAttachmentInput,
  SendMessagePayload,
  SendMessageResponse,
} from '../../types/messages';
// Import icons (matching old prairi-web design)
import locationIcon from '../../assets/icons/location.png';
import industryIcon from '../../assets/icons/industry.png';
import bizModelIcon from '../../assets/icons/biz_model.gif';
import stageIcon from '../../assets/icons/stage.gif';
import amountIcon from '../../assets/icons/amount.gif';
import pitchIcon from '../../assets/icons/pitch.png';
import starIcon from '../../assets/icons/star.png';
import foundersIcon from '../../assets/icons/founders.png';
import pressIcon from '../../assets/icons/press.png';
import ownershipIcon from '../../assets/icons/ownership.png';
import existingInvestorsIcon from '../../assets/handshake__white_.png';
import tractionIcon from '../../assets/icons/traction.png';
import expertiseIcon from '../../assets/icons/expertise.png';
import supportIcon from '../../assets/icons/support.png';
import sdgIcon from '../../assets/icons/sdglogo.png';
import closeXGreyIcon from '../../assets/icons/close-x-grey.png';
import favoriteHeart from '../../assets/favorites_icon__red_heart__right_red.png';
import shareIcon from '../../assets/share_icon2.png';
import prairiUnicorn from '../../assets/prairi-unicorn.png';
import profileCardFade from '../../assets/profile-card-fade.png';

const founderRepresentationLabels: { [key: string]: string } = {
  woman: 'Woman',
  poc: 'POC',
  lgbtq: 'LGBTQ+',
  immigrant: 'Immigrant',
  personWithDisabilities: 'Person with disabilities',
  seniorEntrepreneur: 'Senior/65+',
  teenEntrepreneur: 'Teen/19-',
};

/**
 * When viewing from "Your favorites" or "Previously viewed", show the stored prior
 * browse row — not a membership guess (Women-led / geography), which was wrong for
 * startups opened from SFY / LI / Based on Favorites.
 */
function resolveBrowseContextBadgeCategory(profile: StartupProfileType): CatalogCategory {
  const fromStored = profile.originalCategory?.trim();
  if (fromStored) return normalizeSinceYouFollowLabel(fromStored) as CatalogCategory;
  return CATALOG_CATEGORY_LABELS.speciallyForYou;
}

interface InfoRowProps {
  icon: string;
  value: string | React.ReactNode;
  label?: string;
  dense?: boolean;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, value, label, dense = false }) => {
  if (!value) return null;

  return (
    <div
      className={`flex items-start ${dense ? 'space-x-2.5 py-0.5 lg:space-x-4 lg:py-1.5' : 'space-x-4 py-1.5'}`}
    >
      <div
        className={`flex flex-shrink-0 items-center justify-center ${
          dense ? 'h-5 w-5 lg:h-8 lg:w-8' : 'h-8 w-8'
        }`}
      >
        <img src={icon} alt="" className="h-full w-full object-contain" loading="lazy" />
      </div>
      <div className="min-w-0 flex-1 text-left">
        {label && <div className="mb-1 text-sm text-gray-400">{label}</div>}
        <div
          className={`break-words leading-tight text-white ${dense ? 'text-sm lg:text-base' : 'text-base'}`}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

interface StartupProfileProps {
  profile?: StartupProfileType | null;
  compactDeck?: boolean; // If true, deck height is reduced to allow thumbnails to show partially
  onClose?: () => void;
  /** When provided and user clicks X on a non-favorited profile, dismiss from the dashboard feed. */
  onDismissFromFeedRequested?: (info: { startupId: string; companyName: string }) => void;
  hideActions?: boolean; // If true, hides action buttons (favorites, next match)
  /** Forces single-column layout and replaces the inline pitch deck (tile + right-column iframe) with a plain "View pitch deck" link. */
  pitchDeckOnDemand?: boolean;
  /** Shared / email link view: smaller SDG tiles so they don’t dominate the card. */
  compactSdgs?: boolean;
  /** Shared profile: stretch the black column gutter up into the page top padding. */
  flushColumnGutter?: boolean;
  /** Inbox: green while this startup is in PRAIRI right now, white when they are not. */
  isOnline?: boolean;
  // Tracking props (optional)
  trackingSource?: 'catalog' | 'match' | 'search' | 'recommendation' | 'favorites';
  trackingCategory?: CatalogCategory;
  // Optional: If parent component already knows if this startup is favorited
  isFavorited?: boolean;
  // Callback cuando cambia el estado de favoritos (startupId, isFavorited)
  onFavoriteChange?: (startupId: string, isFavorited: boolean) => void;
  // Callback when user has scrolled enough to consider the profile "viewed"
  onMarkViewed?: (startupId: string) => void;
  /** When provided, renders at the top of the left column (above profile card content). */
  leftPanelHeader?: React.ReactNode;
  /** When provided, renders in place of the pitch-deck iframe in the right column. */
  rightPanel?: React.ReactNode;
  /** Favorites: render above the built-in pitch (thumbnails). Pitch stays dashboard-sized. */
  abovePitchContent?: React.ReactNode;
  /** When true, the left card grows to fill the viewport (minus nav + 20px) instead of the default 85vh cap. */
  leftPanelTall?: boolean;
  /** Background class applied to the compactDeck section wrappers. Defaults to bg-[#1d1d1d]. */
  compactBgClass?: string;
  /** Admin pending review: unsaved thumbnail preview from the approval flow. */
  previewThumbnailUrl?: string;
  /** Admin pending-approval review layout (pitch deck tile in sidebar when no video). */
  adminPendingReview?: boolean;
  /**
   * When set, “View pitch deck” calls this instead of opening the PDF URL
   * (e.g. inbox → navigate to dashboard profile in the same tab).
   */
  onOpenFullProfile?: () => void;
  /** Inbox thread: startup is already a favorite, so Contact stays green and does not open a popup. */
  contactAlreadyOpen?: boolean;
  /** Messages column: a bit wider padding and an inside scrollbar. */
  messagesPane?: boolean;
}

const StartupProfile: React.FC<StartupProfileProps> = ({
  profile: externalProfile,
  compactDeck = false,
  onClose,
  onDismissFromFeedRequested,
  hideActions = false,
  pitchDeckOnDemand = false,
  compactSdgs = false,
  flushColumnGutter = false,
  isOnline,
  trackingSource = 'catalog',
  trackingCategory,
  isFavorited: initialIsFavorited,
  onFavoriteChange,
  onMarkViewed,
  leftPanelHeader,
  rightPanel,
  abovePitchContent,
  leftPanelTall = false,
  compactBgClass = 'bg-[#1d1d1d]',
  previewThumbnailUrl,
  adminPendingReview = false,
  onOpenFullProfile,
  contactAlreadyOpen = false,
  messagesPane = false,
}) => {
  const { startupProfile: contextProfile, investorProfile, loading } = useProfile();
  const startupProfile = externalProfile !== undefined ? externalProfile : contextProfile;
  const { user } = useAuth();
  const {
    startViewTracking,
    endViewTracking,
    trackScrollProfile,
    trackScrollPitchDeck,
    trackClickWebsite,
    trackClickLink,
  } = useTracking();
  const {
    addToFavorites,
    removeFromFavorites,
    checkIsFavorited,
    setStartupDisclosureConsent,
    isLoading: favoritesLoading,
  } = useFavorites();

  const {
    dealStatus,
    disclosureConsent,
    isLoading: dealStatusLoading,
    isUpdating: dealStatusUpdating,
    hasStartupFeedback,
    refreshStatus: refreshDealStatus,
    updateDealStatus,
    resetToUnfollowed,
    restoreLocalStatus,
  } = useDealTracking({ startupId: startupProfile?.id });

  // Guards setState after unmount for async action handlers
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Estado local para saber si está en favoritos
  const [isFavorited, setIsFavorited] = useState(initialIsFavorited ?? false);

  const [isCheckingFavorite, setIsCheckingFavorite] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showRemoveFavoritePopup, setShowRemoveFavoritePopup] = useState(false);
  const [showDismissFromFeedPopup, setShowDismissFromFeedPopup] = useState(false);
  const [showFavoriteConsentPopup, setShowFavoriteConsentPopup] = useState(false);
  const [messageSending, setMessageSending] = useState(false);
  const [messageSendError, setMessageSendError] = useState<string | null>(null);
  const [showContactMessagePopup, setShowContactMessagePopup] = useState(false);
  const [startupUserName, setStartupUserName] = useState<string | null>(null);
  const [showContactUnlockedToast, setShowContactUnlockedToast] = useState(false);
  const [contactToastMessage, setContactToastMessage] = useState('');
  const [showSharePopup, setShowSharePopup] = useState(false);

  // Sincronizar estado cuando cambie el prop
  const wasFavoritedRef = useRef(initialIsFavorited ?? false);
  useEffect(() => {
    if (initialIsFavorited !== undefined) {
      setIsFavorited(initialIsFavorited);
    }
    const next = initialIsFavorited ?? false;
    if (next && !wasFavoritedRef.current) {
      void refreshDealStatus();
    }
    wasFavoritedRef.current = next;
  }, [initialIsFavorited, refreshDealStatus]);

  // Cargar estado inicial de favoritos cuando el componente se monta
  // Solo si no viene como prop, tenemos un startupId, y las acciones son visibles
  useEffect(() => {
    if (
      !startupProfile?.id ||
      initialIsFavorited !== undefined ||
      isCheckingFavorite ||
      hideActions
    )
      return;

    const loadFavoriteStatus = async () => {
      setIsCheckingFavorite(true);
      try {
        const favorited = await checkIsFavorited(startupProfile.id!);
        setIsFavorited(favorited);
      } catch (error) {
        console.error('Error loading favorite status:', error);
      } finally {
        setIsCheckingFavorite(false);
      }
    };

    loadFavoriteStatus();
  }, [startupProfile?.id, checkIsFavorited, initialIsFavorited, isCheckingFavorite]);

  useEffect(() => {
    if (!showContactUnlockedToast) return;
    const t = setTimeout(() => setShowContactUnlockedToast(false), 4000);
    return () => clearTimeout(t);
  }, [showContactUnlockedToast]);

  useEffect(() => {
    if (!showContactMessagePopup || !startupProfile?.userId) return;
    // Prefer foundersData names; only fetch account name as fallback.
    const fromFounders = formatFounderLine(startupProfile.foundersData);
    if (fromFounders) {
      setStartupUserName(null);
      return;
    }

    let cancelled = false;
    const loadStartupUserName = async () => {
      try {
        const functions = getFunctions();
        const getUser = httpsCallable<
          { userId: string },
          { success: boolean; user?: { name?: string } }
        >(functions, 'getUser');
        const result = await getUser({ userId: startupProfile.userId! });
        if (cancelled) return;
        const name = result.data?.success ? result.data.user?.name : undefined;
        setStartupUserName(typeof name === 'string' && name.trim().length > 0 ? name : null);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading startup user name:', error);
          setStartupUserName(null);
        }
      }
    };

    loadStartupUserName();
    return () => {
      cancelled = true;
    };
  }, [showContactMessagePopup, startupProfile?.userId, startupProfile?.foundersData]);

  const messageFounderLine = useMemo(() => {
    const fromFounders = formatFounderLine(startupProfile?.foundersData);
    if (fromFounders) return fromFounders;
    return startupUserName ? `Founder: ${startupUserName}` : null;
  }, [startupProfile?.foundersData, startupUserName]);

  // Refs para trackear scroll
  const profileContainerRef = useRef<HTMLDivElement>(null);
  const profilePanelRef = useRef<HTMLDivElement>(null);
  const pitchDeckPanelRef = useRef<HTMLDivElement>(null);
  const inlinePitchDeckSectionRef = useRef<HTMLDivElement>(null);
  const lastScrollDepthRef = useRef<number>(0);
  const lastPitchDeckScrollDepthRef = useRef<number>(0);
  /** If the profile card never scrolls (short content), still record viewed profile once for the dashboard */
  const profileScrollFallbackSentRef = useRef(false);
  /** Reveal pitch deck in the profile layout (same tab) instead of opening a PDF download tab. */
  const [pitchDeckExpanded, setPitchDeckExpanded] = useState(false);
  /** Host node for the pitch scroll pill — sits outside the grey deck, zero size impact. */
  const [pitchScrollbarHost, setPitchScrollbarHost] = useState<HTMLDivElement | null>(null);
  /** Shared pill height (px) from the profile card track — pitch deck matches this. */
  const [sharedScrollbarThumbPx, setSharedScrollbarThumbPx] = useState(0);
  const [dismissTooltipPos, setDismissTooltipPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const [shareTooltipPos, setShareTooltipPos] = useState<{ top: number; left: number } | null>(
    null
  );

  // Reset revealed deck when switching startups
  useEffect(() => {
    setPitchDeckExpanded(false);
    setDismissTooltipPos(null);
    setShareTooltipPos(null);
  }, [startupProfile?.id]);

  // Keep profile + pitch deck thumbs the same pixel size (profile track × fraction).
  // Inbox messages pane uses a slightly larger shared pill so it matches the chat/list bars.
  useEffect(() => {
    if (!compactDeck) return;
    const el = profilePanelRef.current;
    if (!el) return;
    const fraction = messagesPane ? 0.12 : PROFILE_PITCH_THUMB_TRACK_FRACTION;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h <= 0) return;
      setSharedScrollbarThumbPx(Math.max(20, Math.round(h * fraction)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [compactDeck, messagesPane, startupProfile?.id]);

  useEffect(() => {
    if (startupProfile?.pitchDeckUrl) prefetchPitchDeck(startupProfile.pitchDeckUrl);
  }, [startupProfile?.pitchDeckUrl]);

  // Admin approve / Users review uses hideActions — do not log those views as investor activity.
  const trackInvestorActivity = user?.userType === 'investor' && !hideActions;

  // Track "view" when component mounts and unmounts
  useEffect(() => {
    if (!startupProfile?.id || !trackInvestorActivity) return;

    const openedAt = Date.now();

    // Start tracking when component mounts (solo guarda el tiempo)
    startViewTracking(startupProfile.id, {
      source: trackingSource,
      category: trackingCategory,
    });

    // End tracking when component unmounts (cleanup function)
    // Esto envía el evento con la duración total
    return () => {
      const durationMs = Date.now() - openedAt;
      endViewTracking(startupProfile.id, {
        source: trackingSource,
        category: trackingCategory,
      });
      if (durationMs >= 1500 && onMarkViewed) {
        onMarkViewed(startupProfile.id!);
      }
    };
  }, [
    startupProfile?.id,
    trackInvestorActivity,
    trackingSource,
    trackingCategory,
    startViewTracking,
    endViewTracking,
    onMarkViewed,
  ]);

  // Track scroll in profile container
  useEffect(() => {
    if (!startupProfile?.id || !trackInvestorActivity || !profileContainerRef.current) return;

    const container = profileContainerRef.current;

    // Throttle function para no trackear demasiado frecuentemente
    let throttleTimeout: NodeJS.Timeout | null = null;
    const handleScroll = () => {
      if (throttleTimeout) return;

      throttleTimeout = setTimeout(() => {
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        const scrollDepth = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);

        // Solo trackear si el scroll depth cambió significativamente (mínimo 10%)
        if (Math.abs(scrollDepth - lastScrollDepthRef.current) >= 10) {
          lastScrollDepthRef.current = scrollDepth;
          trackScrollProfile(startupProfile.id!, scrollDepth);
          profileScrollFallbackSentRef.current = true;
          if (scrollDepth >= 10 && onMarkViewed) {
            onMarkViewed(startupProfile.id!);
          }
        }

        throttleTimeout = null;
      }, 500); // Throttle de 500ms
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [startupProfile?.id, trackInvestorActivity, trackScrollProfile, onMarkViewed]);

  useEffect(() => {
    profileScrollFallbackSentRef.current = false;
  }, [startupProfile?.id]);

  useEffect(() => {
    if (!startupProfile?.id) return;
    void logAnalyticsStartupEvent('view_startup', {
      startupId: startupProfile.id,
      country: startupProfile.country,
      industry: startupProfile.industry,
      source: trackingSource,
    });
  }, [startupProfile?.id, startupProfile?.country, startupProfile?.industry, trackingSource]);

  // Short profiles: inner card may not scroll; after a moment, send one scroll-profile so startup dashboard can show activity
  useEffect(() => {
    if (!startupProfile?.id || !compactDeck || !trackInvestorActivity) return;

    const startupId = startupProfile.id;
    const timer = window.setTimeout(() => {
      if (profileScrollFallbackSentRef.current) return;
      const el = profileContainerRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 16) {
        trackScrollProfile(startupId, 12);
        profileScrollFallbackSentRef.current = true;
        onMarkViewed?.(startupId);
      }
    }, 2000);

    return () => window.clearTimeout(timer);
  }, [startupProfile?.id, compactDeck, trackInvestorActivity, trackScrollProfile, onMarkViewed]);

  const handlePitchDeckScrollDepth = (scrollDepth: number) => {
    if (!startupProfile?.id || !trackInvestorActivity) return;
    if (Math.abs(scrollDepth - lastPitchDeckScrollDepthRef.current) < 10) return;
    lastPitchDeckScrollDepthRef.current = scrollDepth;
    trackScrollPitchDeck(startupProfile.id, scrollDepth);
    if (scrollDepth >= 10) onMarkViewed?.(startupProfile.id);
  };

  // Handler para trackear click en website
  const handleWebsiteClick = (url: string) => {
    if (!startupProfile?.id || !trackInvestorActivity) return;
    trackClickWebsite(startupProfile.id, url);
  };

  // Handler para trackear click en otros links (pitch deck, social media, etc.)
  const handleLinkClick = (url: string, elementType: string) => {
    if (!startupProfile?.id || !trackInvestorActivity) return;
    trackClickLink(startupProfile.id, url, elementType);
  };

  /**
   * “View pitch deck” → stay in / go to the profile view in the same tab.
   * Never open the raw PDF in a new tab (downloadable).
   */
  const openPitchDeckInProfile = () => {
    if (!startupProfile?.pitchDeckUrl) return;
    handleLinkClick(startupProfile.pitchDeckUrl, 'pitch-deck-link');
    if (trackInvestorActivity && startupProfile.id) {
      trackScrollPitchDeck(startupProfile.id, 25);
    }

    // e.g. Messages sidebar → full dashboard profile
    if (onOpenFullProfile) {
      onOpenFullProfile();
      return;
    }

    // Already on profile: reveal the deck panel (including on mobile) and scroll to it
    setPitchDeckExpanded(true);
    requestAnimationFrame(() => {
      const target = pitchDeckPanelRef.current || inlinePitchDeckSectionRef.current;
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Handler para acción principal del tracker (add to favorites / avanzar paso / remover)
  const handleFavoriteClick = async () => {
    if (!startupProfile?.id || !user || user.userType !== 'investor') return;

    // Not an active favorite — including a startup they removed earlier.
    // Do not treat dealStatus alone as "add": a stale heart + not-contacted used to
    // re-follow when the user meant to unfollow.
    if (!isFavorited) {
      // Optimistic UI: flip the button and show feedback immediately, then
      // persist in the background so the click feels instant.
      setIsFavorited(true);
      restoreLocalStatus('favorited');
      const inboxRefollow = messagesPane;
      if (!inboxRefollow) {
        setShowFavoriteConsentPopup(true);
        setContactToastMessage('Followed. Find them in your Favorites');
        setShowContactUnlockedToast(true);
      }
      onFavoriteChange?.(startupProfile.id, true);

      void (async () => {
        try {
          const result = await addToFavorites(startupProfile.id, {
            source: trackingSource,
            category: trackingCategory,
            country: startupProfile.country,
            industry: startupProfile.industry,
          });
          if (result.success) {
            // Re-assert after any mid-flight prop flicker from a favorites refetch race.
            setIsFavorited(true);
            const alreadyFavorited = result.alreadyFavorited === true;
            if (alreadyFavorited) {
              // Already a favorite (stale heart state): drop the redundant popup/toast.
              setShowFavoriteConsentPopup(false);
              setShowContactUnlockedToast(false);
            }
            if (inboxRefollow) {
              try {
                await updateDealStatus('contacted');
              } catch {
                // Follow still succeeds if the tracker write fails.
              }
            }
            void refreshDealStatus();
          } else {
            // Revert optimistic state on a failed write.
            console.error('Error adding to favorites:', result.error);
            setIsFavorited(false);
            resetToUnfollowed();
            setShowFavoriteConsentPopup(false);
            setShowContactUnlockedToast(false);
            onFavoriteChange?.(startupProfile.id, false);
          }
        } catch (error) {
          console.error('Error adding to favorites:', error);
          setIsFavorited(false);
          resetToUnfollowed();
          setShowFavoriteConsentPopup(false);
          setShowContactUnlockedToast(false);
          onFavoriteChange?.(startupProfile.id, false);
        }
      })();
      return;
    }

    // Advance through deal tracker steps
    const nextStatus = getNextInvestorDealStatus(dealStatus);
    if (!nextStatus) {
      requestRemoveFavorite();
      return;
    }

    if (dealStatus === 'favorited') {
      setShowContactMessagePopup(true);
      return;
    }

    try {
      await updateDealStatus(nextStatus);
    } catch (error) {
      console.error('Error updating deal status:', error);
    }
  };

  const handleRewindDealStep = async (step: typeof dealStatus) => {
    if (dealStatusUpdating || step === dealStatus) return;
    try {
      await updateDealStatus(step);
    } catch (error) {
      console.error('Error moving deal status back:', error);
    }
  };

  const handleRemoveFavorite = async (payload: RemoveFavoriteFeedbackPayload | null) => {
    const startupId = startupProfile?.id;
    if (!startupId) return;

    // Optimistic UI: close the popup, flip to unfollowed, and clear the tracker
    // immediately. Persist in the background and revert heart + tracker on failure.
    const previousDealStatus = dealStatus;
    setShowRemoveFavoritePopup(false);
    setIsFavorited(false);
    resetToUnfollowed();
    onFavoriteChange?.(startupId, false);

    void (async () => {
      try {
        if (payload && payload.reasons.length > 0) {
          const functions = getFunctions();
          const submitStartupFeedback = httpsCallable<
            { startupId: string; reasons: string[]; otherReason?: string | null },
            { success: boolean; error?: string }
          >(functions, 'submitStartupFeedback');
          const result = await submitStartupFeedback({
            startupId,
            reasons: payload.reasons,
            otherReason: payload.otherReason ?? null,
          });
          if (!result.data?.success) {
            console.error('Error submitting feedback:', result.data?.error);
          }
        }

        const removeResult = await removeFromFavorites(startupId);
        if (removeResult.success) {
          void refreshDealStatus();
        } else {
          // Revert the optimistic unfollow on a failed write.
          console.error('Error removing from favorites:', removeResult.error);
          if (mountedRef.current) {
            setIsFavorited(true);
            restoreLocalStatus(previousDealStatus);
            onFavoriteChange?.(startupId, true);
          }
        }
      } catch (error) {
        console.error('Error removing favorite:', error);
        if (mountedRef.current) {
          setIsFavorited(true);
          restoreLocalStatus(previousDealStatus);
          onFavoriteChange?.(startupId, true);
        }
      }
    })();
  };

  function requestRemoveFavorite() {
    setShowRemoveFavoritePopup(true);
  }

  function confirmDismissFromFeed() {
    if (!startupProfile || !onDismissFromFeedRequested) {
      onClose?.();
      return;
    }
    const startupId = resolveStartupId(startupProfile);
    if (!startupId) {
      onClose?.();
      return;
    }
    onDismissFromFeedRequested({
      startupId,
      companyName: startupProfile.companyName || 'This startup',
    });
  }

  function requestDismissFromFeed() {
    setShowDismissFromFeedPopup(true);
  }

  const handleFavoriteConsentAllow = async () => {
    const startupId = startupProfile ? resolveStartupId(startupProfile) : undefined;
    if (!startupId) return;
    try {
      const result = await setStartupDisclosureConsent(startupId, 'granted');
      if (!result.success) {
        console.warn('[favorite-consent] allow returned success=false', result.error);
      }
    } catch (error) {
      console.error('Error saving favorite consent (granted):', error);
    }
    await refreshDealStatus();
    setShowFavoriteConsentPopup(false);
  };

  const handleFavoriteConsentNotNow = async () => {
    const startupId = startupProfile ? resolveStartupId(startupProfile) : undefined;
    if (!startupId) return;
    try {
      const result = await setStartupDisclosureConsent(startupId, 'denied');
      if (!result.success) {
        console.warn('[favorite-consent] not-now returned success=false', result.error);
      }
    } catch (error) {
      console.error('Error saving favorite consent (denied):', error);
    }
    await refreshDealStatus();
    setShowFavoriteConsentPopup(false);
  };

  const handleSendStartupMessage = async (
    message: string,
    attachment?: MessageAttachmentInput | null
  ) => {
    const startupId = startupProfile ? resolveStartupId(startupProfile) : undefined;
    if (!startupId || !user || user.userType !== 'investor') {
      setMessageSendError('Could not send message');
      return false;
    }
    setMessageSendError(null);
    setMessageSending(true);
    try {
      // Messaging reveals the investor — grant even if consent was still unknown/null.
      if (disclosureConsent !== 'granted') {
        await setStartupDisclosureConsent(startupId, 'granted');
      }
      const functions = getFunctions();
      const sendMessage = httpsCallable<SendMessagePayload, SendMessageResponse>(
        functions,
        'sendMessage'
      );

      const result = await sendMessage({
        recipientId: startupId,
        body: message,
        attachment: attachment ?? null,
      });
      if (!mountedRef.current) return false;
      const data = result.data;
      if (!data?.success) {
        setMessageSendError(data?.error || 'Could not send message');
        return false;
      }

      setShowContactMessagePopup(false);
      try {
        const AudioCtx =
          window.AudioContext ||
          (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = 110;
          gain.gain.value = 0.0001;
          osc.connect(gain);
          gain.connect(ctx.destination);
          const now = ctx.currentTime;
          gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
          osc.start(now);
          osc.stop(now + 0.3);
          window.setTimeout(() => void ctx.close(), 400);
        }
      } catch {
        // Sound is optional.
      }
      setContactToastMessage('Message sent');
      setShowContactUnlockedToast(true);
      if (user.id) markSentFounderMessage(user.id);

      updateDealStatus('met')
        .then(() => refreshDealStatus())
        .catch(_err => undefined);
      return true;
    } catch (error) {
      if (!mountedRef.current) return false;
      console.error('Error sending startup message:', error);
      setMessageSendError('Could not send message');
      return false;
    } finally {
      if (mountedRef.current) setMessageSending(false);
    }
  };

  // Memoized derived values — only recomputed when startupProfile reference changes
  const formattedInvestment = useMemo(() => {
    if (!startupProfile?.investment) return null;
    const currency = getDisplayName(CURRENCY_DISPLAY_MAP, startupProfile.currency || 'eur');
    return formatSeekingInvestment(startupProfile.investment, currency);
  }, [startupProfile]);

  const isPrairiBrand = isPrairiTeamName(startupProfile?.companyName);

  const formattedBusinessModel = useMemo(() => {
    if (!startupProfile?.businessModel) return null;
    const businessModel = getDisplayName(BUSINESS_MODEL_DISPLAY_MAP, startupProfile.businessModel);
    const productFocus = startupProfile.productFocus
      ? getDisplayName(PRODUCT_FOCUS_DISPLAY_MAP, startupProfile.productFocus)
      : '';
    if (productFocus && productFocus !== 'Other') return `${businessModel} (${productFocus})`;
    return businessModel;
  }, [startupProfile]);

  const industryLabels = useMemo(
    () => (startupProfile ? startupIndustryLabels(startupProfile) : []),
    [startupProfile]
  );

  const formattedFounders = useMemo(() => {
    if (!startupProfile) return null;
    const foundersCount = startupProfile.companyFounders || 0;
    const founders = startupProfile.foundersData || [];
    const representation = Array.isArray(startupProfile.founderRepresentation)
      ? startupProfile.founderRepresentation
      : [];

    const getArticle = (rep: string): string => {
      const vowels = ['a', 'e', 'i', 'o', 'u'];
      return vowels.includes(rep.charAt(0).toLowerCase()) ? 'an' : 'a';
    };

    const representations = representation
      .map((rep: string) => {
        const label = founderRepresentationLabels[rep]?.trim();
        if (!label || /^_+$/.test(label)) return null;
        if (label === 'POC' || label === 'LGBTQ+') return `a ${label}`;
        const labelLower = label.toLowerCase();
        return `${getArticle(labelLower)} ${labelLower}`;
      })
      .filter(Boolean);

    if (founders.length === 0 && foundersCount === 0) return null;

    return (
      <div className="space-y-2">
        <div className="text-white">
          {foundersCount}
          {representations.length > 0 && (
            <span className="text-white text-sm ml-1">
              {' '}
              (one or more who is {representations.join(', ')}.)
            </span>
          )}
        </div>
        {founders.length > 0 && (
          <>
            {founders.map(
              (
                founder: {
                  name?: string;
                  jobTitle?: string;
                  country?: string;
                  experience?: string;
                  ownership?: string | number;
                  industryExperience?: string;
                  technicalExperience?: string;
                },
                index: number
              ) => {
                // Country is collected on the form but not shown to investors.
                const founderName = founder.name?.trim() || '';
                const founderTitle = founder.jobTitle?.trim() || '';
                const nameAndTitle = founderName
                  ? founderTitle
                    ? `${founderName} (${founderTitle})`
                    : founderName
                  : founderTitle || 'Founder';
                const experience = founder.experience?.trim();
                const industryExperience = founder.industryExperience?.trim();
                const technicalExperience = founder.technicalExperience?.trim();
                const ownershipRaw =
                  typeof founder.ownership === 'number'
                    ? String(founder.ownership)
                    : founder.ownership?.trim() || '';
                const ownership = ownershipRaw
                  .replace(/^ownership:\s*/i, '')
                  .replace(/%$/, '')
                  .trim();
                const ownershipLabel =
                  /^\d{1,3}$/.test(ownership) && Number(ownership) <= 100 ? `${ownership}%` : '';
                return (
                  <div key={index} className="text-white">
                    <div>{nameAndTitle}</div>
                    {ownershipLabel && (
                      <div className="text-gray-300 text-sm mt-0.5">
                        <span className="text-gray-400">Ownership: </span>
                        {ownershipLabel}
                      </div>
                    )}
                    {experience && (
                      <div className="text-gray-300 text-sm mt-0.5">
                        <span className="text-gray-400">Previous startup names & job title: </span>
                        {experience}
                      </div>
                    )}
                    {industryExperience && (
                      <div className="text-gray-300 text-sm mt-0.5">
                        <span className="text-gray-400">Industry experience: </span>
                        {industryExperience}
                      </div>
                    )}
                    {technicalExperience && (
                      <div className="text-gray-300 text-sm mt-0.5">
                        <span className="text-gray-400">Technical experience: </span>
                        {technicalExperience}
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </>
        )}
      </div>
    );
  }, [startupProfile]);

  const formattedTeamMembers = useMemo(() => {
    if (!startupProfile) return null;
    const countRaw = String(startupProfile.teamMembersCount || '0');
    const count = countRaw === '+3' ? '3+' : countRaw;
    const members = startupProfile.teamMembersData || [];
    if ((count === '0' || !count) && members.length === 0) return null;

    return (
      <div className="space-y-2">
        <div className="text-white">{count}</div>
        {members.map(
          (member: { name?: string; jobTitle?: string; experience?: string }, index: number) => {
            const nameAndTitle =
              [member.name?.trim(), member.jobTitle?.trim()].filter(Boolean).join(', ') ||
              'Team member';
            const experience = member.experience?.trim();
            return (
              <div key={index} className="text-white">
                <div>{nameAndTitle}</div>
                {experience ? (
                  <div className="text-gray-300 text-sm mt-0.5">
                    <span className="text-gray-400">Previous startup names & job title: </span>
                    {experience}
                  </div>
                ) : null}
              </div>
            );
          }
        )}
      </div>
    );
  }, [startupProfile]);

  const formattedExistingInvestors = useMemo(() => {
    if (!startupProfile?.hasExistingInvestors) return null;
    const names = (startupProfile.investorsName || []).map(n => n.trim()).filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'Yes';
  }, [startupProfile]);

  const formattedProgramSupport = useMemo(() => {
    const participated = startupProfile?.hasParticipatedInProgram;
    const isOn =
      participated === true ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (participated as any) === 'true';
    if (!isOn) return null;
    // Firestore may still store a comma string from older profiles.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = (startupProfile as any)?.programNames as string | string[] | undefined;
    const names = Array.isArray(raw)
      ? raw.map(n => String(n).trim()).filter(Boolean)
      : typeof raw === 'string' && raw.trim()
        ? raw
            .split(',')
            .map((n: string) => n.trim())
            .filter(Boolean)
        : [];
    return names.length > 0 ? names.join(', ') : 'Yes';
  }, [startupProfile]);

  const formattedTraction = useMemo(() => {
    if (!startupProfile?.traction) return null;
    // Unfilled create default — do not show "No users/clients yet"
    if (startupProfile.traction === 'no-users' && (startupProfile.lastCompletedStep ?? -1) < 3) {
      return null;
    }
    return getDisplayName(
      TRACTION_DISPLAY_MAP,
      startupProfile.traction as keyof typeof TRACTION_DISPLAY_MAP
    );
  }, [startupProfile]);

  const formattedInvestorExpertise = useMemo(
    () =>
      formatInvestorExpertise(startupProfile?.investorExpertise, startupProfile?.otherExpertise),
    [startupProfile]
  );

  const linksAndMediaList = useMemo(() => {
    return formatLinksAndMedia(startupProfile?.socialMediaUrls, startupProfile?.otherLinks);
  }, [startupProfile?.socialMediaUrls, startupProfile?.otherLinks]);

  const formattedCapitalization = useMemo(() => {
    const cap = startupProfile?.capitalization;
    if (!cap || !cap.trim()) return null;
    const match = ownershipSelectOptions.find(opt => opt.value === cap.trim());
    return match?.name || cap.trim();
  }, [startupProfile?.capitalization]);

  if (loading && externalProfile === undefined) {
    return (
      <div className="min-h-screen bg-[#1d1d1d] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!startupProfile) {
    return (
      <div
        className={`${
          compactDeck && pitchDeckOnDemand
            ? 'h-full min-h-0 flex items-center justify-center overflow-hidden rounded-3xl bg-[#303030]'
            : 'min-h-screen bg-[#1d1d1d] flex items-center justify-center'
        }`}
      >
        <div className="text-center">
          <p className="text-white text-lg">
            {compactDeck && pitchDeckOnDemand ? 'Loading profile…' : 'No profile found'}
          </p>
        </div>
      </div>
    );
  }

  const compactDeckBadgeCategory =
    compactDeck && !leftPanelHeader
      ? trackingSource === 'favorites' && isFavorited
        ? CATALOG_CATEGORY_LABELS.yourFavorites
        : trackingCategory === CATALOG_CATEGORY_LABELS.yourFavorites ||
            trackingCategory === 'Your favorites' ||
            trackingCategory === CATALOG_CATEGORY_LABELS.previouslyViewed
          ? resolveBrowseContextBadgeCategory(startupProfile)
          : trackingCategory
            ? (normalizeSinceYouFollowLabel(trackingCategory) as CatalogCategory)
            : trackingCategory
      : null;

  const compactDeckPanelHeightClass =
    compactDeck && pitchDeckOnDemand
      ? 'h-full min-h-0'
      : compactDeck && abovePitchContent
        ? // Favorites: height comes from thumbs + same pitch host as dashboard
          'lg:min-h-0'
        : leftPanelTall
          ? 'lg:h-[calc(100vh_-_6rem_-_60px)]'
          : 'lg:h-[calc(100vh_-_6rem_-_160px)]';

  // Same sticky footer as catalog/favorites; inbox also passes compactDeck.
  const showCompactDealTracker = compactDeck && user?.userType === 'investor' && !isPrairiBrand;
  /** Phone dashboard: the profile is one page scroll, with the tracker after the fields. */
  const phoneProfileFlow = compactDeck && !pitchDeckOnDemand && !abovePitchContent;

  const trimmedVideoPitchUrl = startupProfile.videoPitchUrl?.trim() ?? '';
  const companyWebsiteUrl = normalizeExternalUrl(startupProfile.url);
  const companyThumbnailUrl =
    previewThumbnailUrl?.trim() || startupProfile.thumbnailUrl?.trim() || '';
  const showRightMediaColumn =
    !rightPanel &&
    !pitchDeckOnDemand &&
    (Boolean(startupProfile.pitchDeckUrl) || Boolean(abovePitchContent));
  // Inbox / pitchDeckOnDemand is a narrow sidebar — always single column (full width).
  // Previously `|| pitchDeckUrl` kept lg:w-[28%] even with no right column, crushing text.
  const useLgTwoColumn = !pitchDeckOnDemand;
  /** Desktop: deck tile hidden when PDF/video is in the right column; admin left uses video when set. */
  const adminLeftShowsVideo = adminPendingReview && Boolean(trimmedVideoPitchUrl);
  const hidePitchDeckTileOnDesktop =
    adminLeftShowsVideo ||
    (!adminPendingReview &&
      (Boolean(trimmedVideoPitchUrl) ||
        Boolean(showRightMediaColumn || rightPanel) ||
        (compactDeck && !(hideActions || adminPendingReview))));

  const pitchDeckTile =
    !pitchDeckOnDemand && startupProfile.pitchDeckUrl ? (
      <div className={`!mt-6 !mb-6 ${hidePitchDeckTileOnDesktop ? 'lg:hidden' : ''}`}>
        <button
          type="button"
          onClick={openPitchDeckInProfile}
          className="group relative block w-full aspect-[16/9] rounded-xl overflow-hidden shadow-2xl transition-transform active:scale-[0.98] border border-gray-800 text-left"
        >
          <div className="absolute inset-0 bg-[#222]">
            {companyThumbnailUrl ? (
              <img
                src={companyThumbnailUrl}
                alt=""
                className="absolute inset-0 h-full w-full object-cover opacity-60 transition-opacity group-hover:opacity-80"
                loading="lazy"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-black">
                <svg
                  className="h-16 w-16 text-gray-700"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path d="M4 6h16v12H4z" />
                </svg>
              </div>
            )}
            <div className="absolute inset-0 bg-black/40" />
          </div>
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center">
            <span className="text-xl font-bold tracking-wide text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              View pitch deck
            </span>
          </div>
        </button>
      </div>
    ) : null;

  // Only used when pitchDeckOnDemand and no onOpenFullProfile (e.g. admin compact sidebar)
  const inlinePitchDeckViewer =
    pitchDeckOnDemand && pitchDeckExpanded && startupProfile.pitchDeckUrl ? (
      <div ref={inlinePitchDeckSectionRef} className="!mt-4 !mb-6">
        <div className="w-full h-[60vh] overflow-hidden rounded-xl border border-gray-700 bg-[#262626]">
          <PitchDeckScrollViewer
            url={startupProfile.pitchDeckUrl}
            onScrollDepth={handlePitchDeckScrollDepth}
          />
        </div>
      </div>
    ) : null;

  const videoPitchTile = trimmedVideoPitchUrl ? (
    <div className="!mt-6 !mb-6">
      <VideoPitchTile
        url={trimmedVideoPitchUrl}
        companyThumbnailUrl={companyThumbnailUrl || undefined}
        onLinkClick={() => handleLinkClick(trimmedVideoPitchUrl, 'video-pitch-link')}
        preferVideoThumbnail
      />
    </div>
  ) : null;

  return (
    <>
      <div
        className={`${
          compactDeck && pitchDeckOnDemand
            ? `h-full min-h-0 ${messagesPane ? 'overflow-visible' : 'overflow-hidden'}`
            : compactDeck || pitchDeckOnDemand
              ? 'overflow-visible'
              : 'overflow-x-hidden'
        } ${
          pitchDeckOnDemand && !compactDeck
            ? 'bg-transparent'
            : `${compactDeck ? 'bg-transparent' : 'bg-[#1d1d1d]'} ${!pitchDeckOnDemand && (compactDeck || trackingCategory || startupProfile.featured) ? `${leftPanelHeader ? 'pt-0' : compactDeck ? 'pt-0' : 'pt-3 lg:pt-4'} pb-0` : 'py-4 lg:py-6'}`
        }`}
      >
        <div
          className={
            pitchDeckOnDemand && !compactDeck
              ? 'px-6 py-6'
              : compactDeck && pitchDeckOnDemand
                ? 'relative flex h-full min-h-0 w-full flex-col px-0 pt-0 pb-0'
                : compactDeck
                  ? 'relative w-full px-3 pt-0 pb-0 lg:px-4'
                  : 'container mx-auto px-4 lg:px-6'
          }
        >
          {/* Desktop: Two columns, Mobile: Single column (pitchDeckOnDemand forces single column) */}
          <div
            className={
              compactDeck
                ? `${compactBgClass} w-full${compactDeck && pitchDeckOnDemand ? ' h-full min-h-0' : ''}`
                : undefined
            }
          >
            <div
              className={`flex flex-col ${
                useLgTwoColumn
                  ? `lg:flex-row ${
                      leftPanelTall || abovePitchContent ? 'lg:items-stretch' : 'lg:items-start'
                    }`
                  : ''
              } ${
                compactDeck
                  ? `gap-4 ${abovePitchContent ? 'relative lg:gap-0' : 'lg:gap-0'} ${compactDeckPanelHeightClass}`
                  : 'gap-4 lg:gap-4'
              } w-full`}
            >
              {/* Left column: Profile Information (~35% on desktop) */}
              <div
                ref={compactDeck ? profilePanelRef : undefined}
                className={`${
                  useLgTwoColumn
                    ? abovePitchContent
                      ? // Half a tab down so the hanging label meets the navbar instead of sliding under it
                        'w-full lg:absolute lg:top-[22px] lg:bottom-0 lg:left-2 lg:z-[1] lg:h-auto lg:w-[calc(28%-1.5rem)]'
                      : 'w-full lg:w-[28%] lg:shrink-0'
                    : 'w-full'
                } ${
                  compactDeck
                    ? `prairi-profile-panel group/profile relative min-h-0 ${
                        abovePitchContent ? 'lg:h-auto min-h-0' : 'h-auto lg:h-full'
                      }`
                    : ''
                }`}
              >
                {/* Badge hangs half above the card so its top meets the navbar. */}
                {leftPanelHeader}
                <div
                  className={`relative ${pitchDeckOnDemand && !compactDeck ? '' : 'mt-0 lg:mt-0'} ${
                    compactDeck && pitchDeckOnDemand
                      ? `h-full min-h-0 self-stretch ${messagesPane ? 'overflow-visible' : 'overflow-hidden'}`
                      : abovePitchContent
                        ? // Fill absolute top/bottom without percentage height overflow
                          'flex h-full min-h-0 flex-col overflow-visible'
                        : !pitchDeckOnDemand && compactDeck
                          ? `lg:sticky ${flushColumnGutter ? 'lg:top-16' : 'lg:top-[6rem]'} lg:mt-0 self-stretch overflow-visible ${
                              phoneProfileFlow ? 'h-auto lg:h-full' : 'h-full'
                            }`
                          : ''
                  }`}
                >
                  <div
                    className={
                      compactDeck
                        ? `${messagesPane ? 'bg-transparent' : compactBgClass} relative flex min-h-0 flex-col ${
                            phoneProfileFlow ? 'h-auto lg:h-full' : 'h-full'
                          } ${
                            pitchDeckOnDemand
                              ? `${messagesPane ? 'overflow-visible' : 'overflow-hidden'} px-0 pb-0 pt-0`
                              : abovePitchContent
                                ? // Small gap so the black line sits just under the card
                                  'overflow-visible px-0 pb-1.5 pt-0'
                                : 'overflow-visible px-3 pb-3 pt-0'
                          }`
                        : ''
                    }
                  >
                    {/* Outside overflow-hidden: half on the card, half up to the navbar */}
                    {compactDeckBadgeCategory && (
                      <ProfileCategoryBadge
                        category={compactDeckBadgeCategory}
                        className="pointer-events-none absolute inset-x-0 top-0 z-[100000] flex justify-center px-2 -translate-y-1/2"
                      />
                    )}
                    <div
                      className={
                        compactDeck
                          ? `relative flex min-h-0 flex-col overflow-visible ${
                              phoneProfileFlow
                                ? 'h-auto flex-none lg:h-full lg:flex-1'
                                : 'min-h-0 flex-1'
                            }`
                          : ''
                      }
                    >
                      <div
                        className={
                          compactDeck
                            ? `relative min-h-0 ${
                                phoneProfileFlow
                                  ? 'h-auto overflow-visible lg:h-full lg:flex-1 lg:overflow-hidden'
                                  : 'flex-1 overflow-hidden'
                              }`
                            : undefined
                        }
                      >
                        <div
                          ref={compactDeck ? profileContainerRef : undefined}
                          className={
                            compactDeck
                              ? `prairi-hide-native-scrollbar ${
                                  phoneProfileFlow
                                    ? 'h-auto overflow-visible lg:h-full lg:overflow-y-scroll'
                                    : 'h-full overflow-y-scroll'
                                } ${messagesPane ? 'pt-1' : ''}`
                              : undefined
                          }
                          onScroll={() => {
                            if (dismissTooltipPos) setDismissTooltipPos(null);
                            if (shareTooltipPos) setShareTooltipPos(null);
                          }}
                        >
                          <div
                            className={
                              compactDeck
                                ? `relative bg-[#303030] ${messagesPane ? '' : 'shadow-2xl'} ${
                                    messagesPane
                                      ? 'px-5 pb-10 pt-10 sm:px-7 sm:pb-12 sm:pt-14 lg:px-8 lg:pb-14 lg:pt-14'
                                      : pitchDeckOnDemand
                                        ? 'px-4 pb-4 pt-10 sm:px-6 lg:px-8 lg:pt-12'
                                        : // Clear share / close icons so the name isn’t cramped under them
                                          phoneProfileFlow
                                          ? 'px-4 pb-2 pt-16 lg:px-10 lg:pb-4 lg:pt-16'
                                          : 'px-4 pb-4 pt-14 sm:px-8 sm:pt-16 lg:px-10 lg:pt-16'
                                  } ${showCompactDealTracker ? 'rounded-b-none rounded-t-3xl' : 'rounded-3xl'}`
                                : pitchDeckOnDemand && !compactDeck
                                  ? 'relative'
                                  : 'relative rounded-3xl border border-gray-800 bg-[#1A1A1A] p-8 shadow-2xl lg:p-10'
                            }
                          >
                            {/* Share with off-platform investor */}
                            {compactDeck &&
                              user?.userType === 'investor' &&
                              !isPrairiBrand &&
                              !hideActions &&
                              resolveStartupId(startupProfile) && (
                                <button
                                  type="button"
                                  onMouseEnter={event => {
                                    setDismissTooltipPos(null);
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    setShareTooltipPos({
                                      top: rect.bottom + 8,
                                      left: rect.left + rect.width / 2,
                                    });
                                  }}
                                  onMouseLeave={() => setShareTooltipPos(null)}
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShareTooltipPos(null);
                                    setShowSharePopup(true);
                                  }}
                                  className={`absolute z-[100] flex h-10 w-10 rounded-full ${
                                    messagesPane
                                      ? 'left-5 -top-1 items-start justify-start'
                                      : 'left-2 top-2 items-center justify-center sm:left-2.5 sm:top-2.5'
                                  }`}
                                  aria-label="Share this profile with another investor"
                                >
                                  <img
                                    src={shareIcon}
                                    alt=""
                                    className="h-7 w-7 object-contain sm:h-8 sm:w-8"
                                    aria-hidden
                                  />
                                </button>
                              )}
                            {/* Heart / dismiss — locked at top corner so it scrolls with content */}
                            {compactDeck &&
                              (isFavorited || onClose || onDismissFromFeedRequested) && (
                                <button
                                  type="button"
                                  onMouseEnter={event => {
                                    setShareTooltipPos(null);
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    setDismissTooltipPos({
                                      top: rect.bottom + 8,
                                      left: rect.left + rect.width / 2,
                                    });
                                  }}
                                  onMouseLeave={() => setDismissTooltipPos(null)}
                                  onClick={() => {
                                    setDismissTooltipPos(null);
                                    const startupId = resolveStartupId(startupProfile);
                                    if (isFavorited) {
                                      requestRemoveFavorite();
                                    } else if (onDismissFromFeedRequested && startupId) {
                                      requestDismissFromFeed();
                                    } else {
                                      onClose?.();
                                    }
                                  }}
                                  className={`absolute z-[100] flex h-10 w-10 rounded-full ${
                                    messagesPane
                                      ? 'right-5 -top-1 items-start justify-end'
                                      : 'right-2 top-2 items-center justify-center sm:right-2.5 sm:top-2.5'
                                  }`}
                                  aria-label={
                                    isFavorited
                                      ? 'Unfollow'
                                      : messagesPane
                                        ? 'Remove'
                                        : 'Remove startup from your Dashboard'
                                  }
                                >
                                  {isFavorited ? (
                                    <img
                                      src={favoriteHeart}
                                      alt=""
                                      className="h-6 w-6 object-contain"
                                    />
                                  ) : (
                                    <img
                                      src={closeXGreyIcon}
                                      alt=""
                                      className="h-4 w-4 object-contain"
                                    />
                                  )}
                                </button>
                              )}
                            {isPrairiBrand && pitchDeckOnDemand && !compactDeck && (
                              <img
                                src={prairiLogo}
                                alt=""
                                className="absolute top-1 right-2 w-7 h-7 object-contain opacity-90"
                              />
                            )}
                            {pitchDeckOnDemand && !compactDeck ? (
                              <div className="mb-6 flex items-start gap-3 sm:gap-4 w-full min-w-0">
                                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                                  <span
                                    className={`inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/40 ${
                                      isOnline === true ? 'bg-emerald-400' : 'bg-white'
                                    }`}
                                  />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h2
                                    className={`text-lg sm:text-xl font-medium text-white text-left leading-snug break-words ${
                                      isPrairiBrand ? 'tracking-[0.35em]' : ''
                                    }`}
                                    style={{ fontFamily: 'Rubik, sans-serif', fontWeight: 500 }}
                                  >
                                    {isPrairiBrand
                                      ? 'PRAIRI'
                                      : startupProfile.companyName || 'Company Name'}
                                  </h2>
                                  {startupProfile.slogan && (
                                    <p
                                      className={`mt-0.5 break-words text-left ${
                                        isPrairiBrand
                                          ? 'text-gray-300 text-sm'
                                          : 'text-white italic'
                                      }`}
                                    >
                                      {startupProfile.slogan}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <>
                                <h1
                                  className={`mb-0.5 flex items-start pr-8 text-left font-medium text-white ${
                                    phoneProfileFlow ? 'text-lg lg:text-2xl' : 'text-xl sm:text-2xl'
                                  }`}
                                  style={{ fontFamily: 'Rubik, sans-serif', fontWeight: 500 }}
                                >
                                  <span className="min-w-0 flex-1 break-words leading-tight">
                                    {isPrairiBrand
                                      ? 'P R A I R I'
                                      : startupProfile.companyName || 'Company Name'}
                                  </span>
                                </h1>
                                {startupProfile.slogan && (
                                  <p className="text-sm sm:text-base text-gray-400 italic font-medium text-left break-words mt-1">
                                    {startupProfile.slogan}
                                  </p>
                                )}
                              </>
                            )}
                            {/* Message this startup */}
                            {user?.userType === 'investor' &&
                              !isPrairiBrand &&
                              startupProfile?.userId &&
                              startupProfile.userId !== user?.id && (
                                <div
                                  className={`${phoneProfileFlow ? 'mt-2 lg:mt-6' : 'mt-3.5 sm:mt-6'} text-left space-y-2`}
                                >
                                  {contactAlreadyOpen ? (
                                    <p className="block text-left underline text-sm font-normal text-emerald-400">
                                      Message this startup
                                    </p>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setMessageSendError(null);
                                        if (isFavorited) setShowContactMessagePopup(true);
                                        else setShowContactModal(true);
                                      }}
                                      className={[
                                        'block text-left underline text-sm font-normal',
                                        isFavorited
                                          ? 'text-emerald-400 hover:text-emerald-300'
                                          : 'text-gray-400 hover:text-white',
                                      ].join(' ')}
                                    >
                                      Message this startup
                                    </button>
                                  )}
                                </div>
                              )}

                            <div
                              className={`space-y-0 ${phoneProfileFlow ? 'mt-2 lg:mt-6' : 'mt-4 sm:mt-6'}`}
                            >
                              <InfoRow
                                dense={phoneProfileFlow}
                                icon={locationIcon}
                                value={startupProfile.country || ''}
                              />
                              <InfoRow
                                dense={phoneProfileFlow}
                                icon={industryIcon}
                                value={
                                  industryLabels.length === 0 ? null : industryLabels.length ===
                                    1 ? (
                                    industryLabels[0]
                                  ) : (
                                    <div className="flex flex-col gap-0.5">
                                      {industryLabels.map(label => (
                                        <div key={label}>{label}</div>
                                      ))}
                                    </div>
                                  )
                                }
                              />
                              <InfoRow
                                dense={phoneProfileFlow}
                                icon={bizModelIcon}
                                value={isPrairiBrand ? 'B2B and/or B2C' : formattedBusinessModel}
                              />
                              {isPrairiBrand && (
                                <InfoRow dense={phoneProfileFlow} icon={productIcon} value="All" />
                              )}
                              <InfoRow
                                dense={phoneProfileFlow}
                                icon={stageIcon}
                                value={
                                  startupProfile.stage
                                    ? getDisplayName(STAGE_DISPLAY_MAP, startupProfile.stage)
                                    : null
                                }
                              />
                              <InfoRow
                                dense={phoneProfileFlow}
                                icon={amountIcon}
                                value={
                                  isPrairiBrand
                                    ? 'Startups seeking: 50k - 100M EUR/USD'
                                    : formattedInvestment
                                }
                              />
                              <InfoRow
                                dense={phoneProfileFlow}
                                icon={pitchIcon}
                                label="Pitch summary:"
                                value={startupProfile.pitchSummary}
                              />
                              {adminPendingReview && !pitchDeckOnDemand && (
                                <>
                                  {videoPitchTile}
                                  {pitchDeckTile}
                                </>
                              )}
                              {/* Pitch deck on-demand: open full profile / reveal deck (same tab), never a PDF tab */}
                              {pitchDeckOnDemand && startupProfile.pitchDeckUrl && (
                                <div className="!mt-4 !mb-4 text-left">
                                  <button
                                    type="button"
                                    onClick={openPitchDeckInProfile}
                                    className="text-md text-emerald-400 hover:text-emerald-300 underline"
                                  >
                                    View pitch deck
                                  </button>
                                </div>
                              )}
                              {pitchDeckOnDemand && inlinePitchDeckViewer}
                              {pitchDeckOnDemand && trimmedVideoPitchUrl && (
                                <div className="mt-2 mb-2 text-left">
                                  <a
                                    href={
                                      trimmedVideoPitchUrl.startsWith('http')
                                        ? trimmedVideoPitchUrl
                                        : `https://${trimmedVideoPitchUrl}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-md text-emerald-400 hover:text-emerald-300 underline"
                                    onClick={() =>
                                      handleLinkClick(trimmedVideoPitchUrl, 'video-pitch-link')
                                    }
                                  >
                                    View video pitch
                                  </a>
                                </div>
                              )}

                              {!adminPendingReview && pitchDeckTile}
                              {!adminPendingReview && videoPitchTile}
                              {companyWebsiteUrl && !isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={companyIcon}
                                  label="Company website:"
                                  value={
                                    <a
                                      href={companyWebsiteUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-400 hover:text-blue-300 underline break-all"
                                      onClick={() => handleWebsiteClick(companyWebsiteUrl)}
                                    >
                                      {startupProfile.url?.trim() || companyWebsiteUrl}
                                    </a>
                                  }
                                />
                              )}
                              {linksAndMediaList.length > 0 && !isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={pressIcon}
                                  label="Links & media:"
                                  value={
                                    <div className="space-y-1">
                                      {linksAndMediaList.map((item, idx) => (
                                        <div key={idx}>
                                          <a
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:text-blue-300 underline break-all text-sm block"
                                            onClick={() =>
                                              handleLinkClick(item.href, 'links-media-link')
                                            }
                                          >
                                            {item.displayUrl}
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                  }
                                />
                              )}
                              <InfoRow
                                dense={phoneProfileFlow}
                                icon={starIcon}
                                label="Impressive factor:"
                                value={startupProfile.impressiveFactor}
                              />
                              {!isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={foundersIcon}
                                  label="Founders:"
                                  value={formattedFounders}
                                />
                              )}
                              {formattedTeamMembers && !isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={foundersIcon}
                                  label="Team members:"
                                  value={formattedTeamMembers}
                                />
                              )}
                              {formattedCapitalization && !isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={ownershipIcon}
                                  label="Capitalization:"
                                  value={formattedCapitalization}
                                />
                              )}
                              {formattedExistingInvestors && !isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={existingInvestorsIcon}
                                  label="This company has existing investors:"
                                  value={formattedExistingInvestors}
                                />
                              )}
                              {formattedProgramSupport && !isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={supportIcon}
                                  label="Participated in a generator, incubator or accelerator:"
                                  value={formattedProgramSupport}
                                />
                              )}
                              {!isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={tractionIcon}
                                  label="Traction:"
                                  value={formattedTraction}
                                />
                              )}
                              {!isPrairiBrand && (
                                <InfoRow
                                  dense={phoneProfileFlow}
                                  icon={expertiseIcon}
                                  label="Appreciates an investor with:"
                                  value={formattedInvestorExpertise}
                                />
                              )}
                            </div>

                            {/* SDGs */}
                            {startupProfile.selectedSDGs &&
                              startupProfile.selectedSDGs.length > 0 && (
                                <div className="flex items-start space-x-4 py-1.5">
                                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                                    <img
                                      src={sdgIcon}
                                      alt="SDGs"
                                      className="w-full h-full object-contain"
                                      loading="lazy"
                                    />
                                  </div>
                                  <div className="flex-1 text-left">
                                    <div className="text-gray-400 text-sm mb-3">
                                      Working towards the following Sustainable Development Goals:
                                    </div>
                                    <div
                                      className={
                                        compactSdgs
                                          ? 'grid max-w-xs grid-cols-4 gap-1.5 sm:max-w-sm sm:grid-cols-5 sm:gap-2'
                                          : 'grid grid-cols-3 gap-3 sm:gap-4'
                                      }
                                    >
                                      {startupProfile.selectedSDGs.map((sdg: number) => (
                                        <div key={sdg} className="min-w-0 w-full">
                                          <SdgGoalImage sdg={sdg} />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                        {compactDeck && !showCompactDealTracker ? (
                          <img
                            src={profileCardFade}
                            alt=""
                            aria-hidden
                            className="pointer-events-none absolute inset-x-0 bottom-0 z-20 w-full object-fill h-20 lg:h-[189px]"
                          />
                        ) : null}
                      </div>
                      {compactDeck && (
                        <div className={phoneProfileFlow ? 'hidden lg:block' : undefined}>
                          <PrairiScrollbar
                            scrollRef={profileContainerRef}
                            hoverRootRef={profilePanelRef}
                            forceShow
                            outside
                            thumbHeight={
                              sharedScrollbarThumbPx > 0 ? sharedScrollbarThumbPx : undefined
                            }
                            constantTrackFraction={
                              sharedScrollbarThumbPx > 0
                                ? undefined
                                : PROFILE_PITCH_THUMB_TRACK_FRACTION
                            }
                          />
                        </div>
                      )}
                      {showCompactDealTracker && (
                        <div className="relative shrink-0 rounded-b-3xl rounded-t-none bg-[#303030]">
                          <img
                            src={profileCardFade}
                            alt=""
                            aria-hidden
                            className="pointer-events-none absolute inset-x-0 bottom-full z-20 w-full object-fill h-20 lg:h-[189px]"
                          />
                          <div
                            aria-hidden
                            className="absolute inset-x-0 top-0 z-30 h-0.5"
                            style={{
                              backgroundColor: PRAIRI_SURFACES.pitchDeck,
                            }}
                          />
                          <div
                            className={
                              messagesPane
                                ? 'px-4 py-4 sm:px-8 lg:px-10'
                                : phoneProfileFlow
                                  ? 'px-3 py-2 lg:px-10 lg:py-4'
                                  : 'px-4 py-4 sm:px-8 lg:px-10'
                            }
                          >
                            <DealTracker
                              userType={USER_TYPES.investor}
                              status={dealStatus}
                              primaryActionLabel={ACTION_LABELS.investor[dealStatus]}
                              onPrimaryActionClick={handleFavoriteClick}
                              onCompletedStepClick={handleRewindDealStep}
                              primaryActionLoading={
                                favoritesLoading || dealStatusUpdating || messageSending
                              }
                              primaryActionDisabled={
                                favoritesLoading ||
                                dealStatusLoading ||
                                dealStatusUpdating ||
                                messageSending
                              }
                              dense={phoneProfileFlow}
                            />
                          </div>
                        </div>
                      )}
                    </div>{' '}
                    {/* Close outer card div */}
                  </div>{' '}
                  {/* Close darker grey rectangle */}
                </div>{' '}
                {/* Close inner sticky wrapper */}
              </div>{' '}
              {/* Close Column 1 div */}
              {/* Favorites: keep 28% width in flow while profile is absolutely pinned */}
              {abovePitchContent && useLgTwoColumn ? (
                <div className="hidden lg:block lg:w-[28%] lg:shrink-0" aria-hidden="true" />
              ) : null}
              {compactDeck &&
              useLgTwoColumn &&
              !abovePitchContent &&
              (Boolean(rightPanel) || showRightMediaColumn) ? (
                <div
                  className={`hidden w-3 shrink-0 bg-black lg:block ${
                    flushColumnGutter
                      ? 'self-stretch lg:-mt-6 lg:h-[calc(100%+1.5rem)]'
                      : leftPanelHeader
                        ? 'lg:-mt-4 lg:h-[calc(100%+1rem)]'
                        : 'self-stretch'
                  }`}
                  aria-hidden="true"
                />
              ) : null}
              {/* Right column: Pitch deck PDF — also shown on mobile after “View pitch deck” */}
              {rightPanel ? (
                <div ref={pitchDeckPanelRef} className="contents">
                  {rightPanel}
                </div>
              ) : (
                showRightMediaColumn && (
                  <div
                    ref={pitchDeckPanelRef}
                    className={`${
                      pitchDeckExpanded
                        ? `flex flex-col max-lg:mt-0 lg:mt-4 ${
                            compactDeck && !pitchDeckOnDemand
                              ? 'max-lg:relative max-lg:left-1/2 max-lg:w-screen max-lg:-translate-x-1/2 lg:mx-0 lg:w-auto'
                              : 'w-full'
                          }`
                        : 'hidden'
                    } lg:flex lg:min-w-0 lg:flex-col ${
                      compactDeck || pitchDeckOnDemand
                        ? abovePitchContent
                          ? // Flush under the sticky header so the black gutter reaches the navbar
                            'lg:flex-1 lg:sticky lg:top-16 lg:mt-0 lg:min-h-0'
                          : `lg:flex-1 lg:sticky ${flushColumnGutter ? 'lg:top-16' : 'lg:top-[6rem]'} lg:mt-0 lg:min-h-0`
                        : 'lg:w-[68%] lg:shrink-0 lg:sticky lg:top-6 lg:mt-4 lg:h-fit lg:ml-auto'
                    } ${
                      abovePitchContent
                        ? 'overflow-visible lg:border-l-[0.75rem] lg:border-black'
                        : 'lg:h-full'
                    }`}
                  >
                    {abovePitchContent ? (
                      <div className="hidden lg:block">{abovePitchContent}</div>
                    ) : null}
                    {abovePitchContent ? (
                      <div
                        className="hidden h-3 w-auto shrink-0 bg-black -mr-3 lg:block lg:-mr-7"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div
                      className={`relative overflow-visible ${
                        compactDeck && !pitchDeckOnDemand
                          ? 'bg-white px-0 lg:bg-[#1d1d1d] lg:px-2'
                          : 'bg-[#1d1d1d] px-2'
                      } ${
                        compactDeck || pitchDeckOnDemand
                          ? abovePitchContent
                            ? // Favorites: same pitch height as dashboard; small pad so the black line sits just under
                              'flex h-[calc(100vh_-_6rem_-_160px)] min-h-0 shrink-0 flex-col pt-0 pb-1.5'
                            : compactDeck && !pitchDeckOnDemand
                              ? // Phone: one full-screen slide, including when the phone is turned. Desktop stays beside the profile.
                                'flex h-[100dvh] w-full flex-none flex-col overflow-hidden pt-0 pb-0 lg:h-[calc(100vh_-_6rem_-_160px)] lg:min-h-0 lg:flex-1'
                              : 'flex h-[calc(100vh_-_6rem_-_160px)] min-h-0 flex-col pt-0 pb-0'
                          : 'pt-0 pb-0 h-[calc(100vh-6rem)]'
                      }`}
                    >
                      <div
                        className={`group/deck relative min-h-0 overflow-hidden [overscroll-behavior:none] ${
                          compactDeck && !pitchDeckOnDemand
                            ? 'h-full flex-none bg-white p-0 lg:min-h-0 lg:flex-1 lg:bg-[#303030] lg:p-2'
                            : 'h-full flex-1 bg-[#303030] p-2'
                        }`}
                      >
                        {startupProfile.pitchDeckUrl && !pitchDeckOnDemand && (
                          <PitchDeckScrollViewer
                            url={startupProfile.pitchDeckUrl}
                            onScrollDepth={handlePitchDeckScrollDepth}
                            matchScrollbarRef={compactDeck ? profileContainerRef : undefined}
                            scrollbarHost={pitchScrollbarHost}
                            scrollbarThumbHeight={
                              sharedScrollbarThumbPx > 0 ? sharedScrollbarThumbPx : undefined
                            }
                            phoneSlides
                            className="relative z-10 h-full min-h-0 overflow-hidden"
                          />
                        )}
                      </div>
                      {/* Outside the grey deck — absolute so deck size/bg stay unchanged */}
                      <div
                        ref={setPitchScrollbarHost}
                        className={`pointer-events-auto absolute top-0 bottom-2 z-50 w-2 overflow-visible ${
                          compactDeck && !pitchDeckOnDemand
                            ? `left-auto right-0.5 ml-0 lg:left-full lg:right-auto ${
                                abovePitchContent ? 'lg:ml-2' : 'lg:ml-0.5'
                              }`
                            : `left-full ${abovePitchContent ? 'ml-2' : 'ml-0.5'}`
                        }`}
                        aria-hidden
                      />
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
          {compactDeck && !pitchDeckOnDemand && (
            <div
              className={`${phoneProfileFlow ? 'hidden lg:block' : ''} h-3 bg-black -mx-3 ${
                abovePitchContent ? 'relative z-[2] lg:-ml-4 lg:-mr-7' : 'lg:-mx-4'
              }`}
              aria-hidden="true"
            />
          )}
        </div>{' '}
        {/* Close Container (645) */}
      </div>{' '}
      {/* Close Outer Div (644) */}
      {showContactUnlockedToast && contactToastMessage && (
        <div
          className="fixed bottom-8 left-1/2 z-[110] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 px-5 py-4 bg-[#1A1A1A] border border-emerald-500/60 rounded-2xl shadow-2xl flex items-center gap-3 sm:bottom-10"
          role="status"
          aria-live="polite"
        >
          <img src={prairiUnicorn} alt="" className="h-6 w-6 flex-shrink-0 object-contain" />
          <p className="text-white text-base font-medium">{contactToastMessage}</p>
        </div>
      )}
      <ContactStartupModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onAddToFavorites={handleFavoriteClick}
        isLoading={favoritesLoading}
      />
      <FavoriteConsentPopup
        isOpen={showFavoriteConsentPopup}
        startupName={startupProfile.companyName || 'this startup'}
        investorPreview={
          user?.userType === 'investor'
            ? {
                name: user.name,
                country: investorProfile?.country || user.country,
                companyName: investorProfile?.companyName,
                photoUrl: investorProfile?.profilePictureUrl,
                expertise: investorProfile?.expertise,
                otherExpertise: (investorProfile as { otherExpertise?: string } | null)
                  ?.otherExpertise,
                previousInvestments: investorProfile?.previousInvestments,
                paysFee: investorProfile?.paysFee,
              }
            : undefined
        }
        onAllow={handleFavoriteConsentAllow}
        onNotNow={handleFavoriteConsentNotNow}
      />
      <ContactStartupMessagePopup
        isOpen={showContactMessagePopup}
        startupName={startupProfile.companyName || 'this startup'}
        thumbnailUrl={startupProfile.thumbnailUrl}
        founderLine={messageFounderLine}
        isSending={messageSending}
        sendError={messageSendError}
        showDisclosureWarning={disclosureConsent === 'denied'}
        recipientId={resolveStartupId(startupProfile) || startupProfile.userId}
        onClose={() => {
          setMessageSendError(null);
          setShowContactMessagePopup(false);
        }}
        onSend={handleSendStartupMessage}
      />
      <RemoveFavoritePopup
        key={showRemoveFavoritePopup ? 'open' : 'closed'}
        isOpen={showRemoveFavoritePopup}
        startupName={startupProfile.companyName || 'This startup'}
        alreadySubmitted={hasStartupFeedback}
        onKeep={() => setShowRemoveFavoritePopup(false)}
        onRemove={handleRemoveFavorite}
        isSending={false}
      />
      <DismissFromFeedPopup
        isOpen={showDismissFromFeedPopup}
        startupName={startupProfile.companyName || 'This startup'}
        onKeep={() => setShowDismissFromFeedPopup(false)}
        onRemove={confirmDismissFromFeed}
      />
      <ShareStartupPopup
        isOpen={showSharePopup}
        startupName={startupProfile.companyName || 'this startup'}
        startupId={resolveStartupId(startupProfile) || ''}
        onClose={() => setShowSharePopup(false)}
        onShared={({ recipientName }) => {
          setContactToastMessage(`Shared with ${recipientName.trim() || 'your colleague'}`);
          setShowContactUnlockedToast(true);
        }}
      />
      {dismissTooltipPos
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none whitespace-nowrap rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-[#111111] shadow-lg"
              style={{
                position: 'fixed',
                top: dismissTooltipPos.top,
                left: dismissTooltipPos.left,
                transform: 'translateX(-85%)',
                zIndex: 2147483647,
              }}
            >
              {isFavorited
                ? 'Unfollow'
                : messagesPane
                  ? 'Remove'
                  : 'Remove startup from your Dashboard'}
            </div>,
            document.body
          )
        : null}
      {shareTooltipPos
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none whitespace-nowrap rounded-lg bg-white px-3 py-1.5 text-sm text-[#111111] shadow-lg"
              style={{
                position: 'fixed',
                top: shareTooltipPos.top,
                left: shareTooltipPos.left,
                transform: 'translateX(-15%)',
                zIndex: 2147483647,
                fontFamily: 'Rubik, sans-serif',
                fontWeight: 500,
              }}
            >
              Share this profile with another investor
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default StartupProfile;
